import { type Dispatch, type SetStateAction, useCallback } from 'react'
import type { PendingChanges } from './use-map-set-sync'

export interface MapSetMutations {
  add: (id: string) => void
  remove: (id: string) => void
  toggle: (id: string) => void
  addMany: (ids: string[]) => void
  removeMany: (ids: string[]) => void
}

interface UseMapSetMutationsArgs {
  endpointIds: Set<string>
  setEndpointIds: Dispatch<SetStateAction<Set<string>>>
  pendingChanges: { current: PendingChanges }
  scheduleSync: () => void
}

export function useMapSetMutations({
  endpointIds,
  setEndpointIds,
  pendingChanges,
  scheduleSync,
}: UseMapSetMutationsArgs): MapSetMutations {
  const add = useCallback(
    (id: string) => {
      setEndpointIds((prev) => {
        if (prev.has(id)) return prev
        const next = new Set(prev)
        next.add(id)
        return next
      })
      pendingChanges.current.add.add(id)
      pendingChanges.current.remove.delete(id)
      scheduleSync()
    },
    [setEndpointIds, pendingChanges, scheduleSync]
  )

  const remove = useCallback(
    (id: string) => {
      setEndpointIds((prev) => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      pendingChanges.current.remove.add(id)
      pendingChanges.current.add.delete(id)
      scheduleSync()
    },
    [setEndpointIds, pendingChanges, scheduleSync]
  )

  const toggle = useCallback(
    (id: string) => {
      if (endpointIds.has(id)) remove(id)
      else add(id)
    },
    [endpointIds, add, remove]
  )

  const addMany = useCallback(
    (ids: string[]) => {
      setEndpointIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) {
          next.add(id)
          pendingChanges.current.add.add(id)
          pendingChanges.current.remove.delete(id)
        }
        return next
      })
      scheduleSync()
    },
    [setEndpointIds, pendingChanges, scheduleSync]
  )

  const removeMany = useCallback(
    (ids: string[]) => {
      setEndpointIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) {
          next.delete(id)
          pendingChanges.current.remove.add(id)
          pendingChanges.current.add.delete(id)
        }
        return next
      })
      scheduleSync()
    },
    [setEndpointIds, pendingChanges, scheduleSync]
  )

  return { add, remove, toggle, addMany, removeMany }
}
